import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Reusable FAQ block with FAQPage JSON-LD injection.
 * `items` = [{ q, a }, ...]
 *
 *  Renders an accordion + a <script type="application/ld+json">.
 *  All HTML content in answers should be plain text (no markup) to be SEO-safe.
 */
const FAQ = ({ title, intro, items, idPrefix = "faq" }) => {
    const [open, setOpen] = useState(0);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };

    return (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid={`${idPrefix}-section`}>
            {(title || intro) && (
                <div className="text-center mb-10">
                    {title && (
                        <h2 className="font-hz-display text-3xl md:text-5xl tracking-tight text-[var(--ink,#0f172a)]">
                            {title}
                        </h2>
                    )}
                    {intro && <p className="text-[var(--ink-soft,#475569)] mt-3 text-base">{intro}</p>}
                </div>
            )}
            <ul className="divide-y divide-[var(--border,#e7e1d0)] border-t border-b border-[var(--border,#e7e1d0)]">
                {items.map((it, i) => {
                    const isOpen = open === i;
                    return (
                        <li key={i} className="py-1">
                            <button
                                type="button"
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                className="w-full flex items-start justify-between gap-4 py-5 text-left group"
                                data-testid={`${idPrefix}-q-${i}`}
                                aria-expanded={isOpen}
                            >
                                <span className="font-hz-display text-lg md:text-xl text-[var(--ink,#0f172a)] leading-snug pr-3">
                                    {it.q}
                                </span>
                                <ChevronDown
                                    className={`w-5 h-5 shrink-0 text-[var(--ink-soft,#475569)] transition mt-1 ${
                                        isOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            <div
                                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                }`}
                            >
                                <div className="overflow-hidden">
                                    <p
                                        className="text-[var(--ink-soft,#475569)] pb-5 pr-8 leading-relaxed text-sm md:text-base"
                                        data-testid={`${idPrefix}-a-${i}`}
                                    >
                                        {it.a}
                                    </p>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
            <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
        </section>
    );
};

export default FAQ;
